// GlobalAlertToasts.jsx — m4a + WebAudio fallback, gated by auth/SSE and first gesture
import React from "react";
import alertM4A from "./Assets/alert.m4a";

/* ---------- tiny utils ---------- */
function useStabilizedBool(value, delayMs = 120) {
  const [stable, setStable] = React.useState(value);
  const t = React.useRef(0);
  React.useEffect(() => {
    if (stable === value) return;
    clearTimeout(t.current);
    t.current = setTimeout(() => setStable(value), delayMs);
    return () => clearTimeout(t.current);
  }, [value, delayMs, stable]);
  return stable;
}

/* ---------- Shared AudioContext (for WebAudio fallback) ---------- */
function useSharedAudioContext() {
  const ctxRef = React.useRef(null);

  const ensure = React.useCallback(() => {
    if (ctxRef.current) return ctxRef.current;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    ctxRef.current = new Ctx();
    return ctxRef.current;
  }, []);

  const resume = React.useCallback(async () => {
    const ctx = ensure();
    if (!ctx) return false;
    if (ctx.state === "suspended") {
      try { await ctx.resume(); } catch {}
    }
    return ctx.state === "running";
  }, [ensure]);

  return { ensure, resume, ctxRef };
}

/* ---------- WebAudio fallback tone ---------- */
function useWebAudioKlaxon(shared) {
  const lastRef = React.useRef(0);

  return React.useCallback(async () => {
    const now = Date.now();
    if (now - (lastRef.current || 0) < 300) return; // rate limit
    lastRef.current = now;
    try {
      const ok = await shared.resume(); // makes sure ctx is running post-gesture
      if (!ok) return;

      const ctx = shared.ensure();
      if (!ctx) return;

      const prefersReduced = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
      const dur = prefersReduced ? 0.45 : 0.65;
      const t0 = ctx.currentTime;

      const master = ctx.createGain(); master.gain.value = prefersReduced ? 0.6 : 4.0; master.connect(ctx.destination);
      const bp = ctx.createBiquadFilter(); bp.type = "bandpass"; bp.frequency.value = 1200; bp.Q.value = 8; bp.connect(master);

      const env = ctx.createGain(); env.gain.setValueAtTime(0.0001, t0); env.gain.exponentialRampToValueAtTime(1.0, t0 + 0.01);
      env.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);

      const osc = ctx.createOscillator(); osc.type = "sine";
      osc.frequency.setValueAtTime(900, t0);
      osc.frequency.exponentialRampToValueAtTime(1400, t0 + dur * 0.7);

      const echoMix = ctx.createGain(); echoMix.gain.value = prefersReduced ? 0.08 : 0.12;
      const delay = ctx.createDelay(); delay.delayTime.value = 0.08;

      osc.connect(env).connect(bp);
      bp.connect(delay).connect(echoMix).connect(master);

      osc.start(t0);
      osc.stop(t0 + dur);
    } catch {}
  }, [shared]);
}

/* ---------- m4a + fallback orchestrator ---------- */
function useM4AWithFallback({ src = alertM4A, volume = 1.0, cooldownMs = 700 } = {}) {
  const sharedCtx = useSharedAudioContext();
  const fallbackTone = useWebAudioKlaxon(sharedCtx);

  const elRef = React.useRef(null);
  const [supportsM4A, setSupportsM4A] = React.useState(false);
  const [ready, setReady] = React.useState(false);
  const [unlocked, setUnlocked] = React.useState(false);
  const [authedReady, setAuthedReady] = React.useState(false); // only show pill after SSE open
  const lastPlayedRef = React.useRef(0);

  // Detect m4a support once
  React.useEffect(() => {
    const a = document.createElement("audio");
    const ok =
      !!a.canPlayType &&
      (a.canPlayType('audio/mp4; codecs="mp4a.40.2"') ||
        a.canPlayType("audio/mp4") ||
        a.canPlayType("audio/aac"));
    setSupportsM4A(!!ok);
  }, []);

  // Listen for auth/SSE state so we don’t show UI before login
  React.useEffect(() => {
    const onOpen = () => setAuthedReady(true);
    const onClose = () => setAuthedReady(false);
    const onUnauth = () => setAuthedReady(false);
    window.addEventListener("sse:open", onOpen);
    window.addEventListener("sse:close", onClose);
    window.addEventListener("unauthenticated", onUnauth);
    return () => {
      window.removeEventListener("sse:open", onOpen);
      window.removeEventListener("sse:close", onClose);
      window.removeEventListener("unauthenticated", onUnauth);
    };
  }, []);

  // Create hidden <audio> if supported
  React.useEffect(() => {
    if (!supportsM4A) return;
    const el = document.createElement("audio");
    elRef.current = el;

    el.preload = "auto";
    el.controls = false;
    el.loop = false;
    el.muted = false;
    el.volume = Math.max(0, Math.min(1, volume));
    el.playsInline = true;
    el.src = src;

    el.setAttribute("tabindex", "-1");
    el.setAttribute("aria-hidden", "true");
    Object.assign(el.style, {
      position: "fixed",
      width: 0,
      height: 0,
      opacity: 0,
      pointerEvents: "none",
      left: "-9999px",
      top: "-9999px",
    });

    const onCanPlay = () => setReady(true);
    el.addEventListener("canplaythrough", onCanPlay, { once: true });

    document.body.appendChild(el);
    try { el.load(); } catch {}

    return () => {
      try { el.pause(); } catch {}
      try { el.remove(); } catch {}
      elRef.current = null;
    };
  }, [supportsM4A, src, volume]);

  // Auto-unlock on first app gesture: unlock BOTH <audio> and WebAudio context
  React.useEffect(() => {
    const attempt = async () => {
      // resume WebAudio so fallback is allowed after the first gesture
      await sharedCtx.resume();

      // try to unlock the <audio> element too
      const el = elRef.current;
      if (supportsM4A && el && !unlocked) {
        try {
          await el.play();
          el.pause(); el.currentTime = 0;
          setUnlocked(true);
        } catch {
          // still locked — pill may appear (but only when authedReady)
        }
      }
    };
    const opts = { once: true, capture: true };
    window.addEventListener("pointerdown", attempt, opts);
    window.addEventListener("keydown", attempt, opts);
    window.addEventListener("touchstart", attempt, opts);
    return () => {
      window.removeEventListener("pointerdown", attempt, { capture: true });
      window.removeEventListener("keydown", attempt, { capture: true });
      window.removeEventListener("touchstart", attempt, { capture: true });
    };
  }, [supportsM4A, unlocked, sharedCtx]);

  const requestUnlock = React.useCallback(async () => {
    // also resume WebAudio so fallback is good to go
    await sharedCtx.resume();

    if (!supportsM4A) return false;
    const el = elRef.current;
    if (!el) return false;
    try {
      await el.play();
      el.pause(); el.currentTime = 0;
      setUnlocked(true);
      return true;
    } catch {
      return false;
    }
  }, [supportsM4A, sharedCtx]);

  const play = React.useCallback(async () => {
    const now = Date.now();
    if (now - (lastPlayedRef.current || 0) < cooldownMs) return false;
    lastPlayedRef.current = now;

    // Prefer m4a if supported & unlocked
    if (supportsM4A && unlocked && elRef.current) {
      try {
        try { elRef.current.currentTime = 0; } catch {}
        await elRef.current.play();
        return true;
      } catch {
        // fall through
      }
    }

    // Fallback: WebAudio tone (works after first gesture)
    await fallbackTone();
    return true;
  }, [cooldownMs, supportsM4A, unlocked, fallbackTone]);

  return {
    play,
    // Show the pill only when we’re authed/SSE-connected AND m4a is supported but still locked
    showUnlockPill: authedReady && supportsM4A && !unlocked,
    ready,
    requestUnlock,
  };
}

/* ---------- UI component ---------- */
export default function GlobalAlerts() {
  const [toasts, setToasts] = React.useState([]);
  const { play, showUnlockPill, ready, requestUnlock } = useM4AWithFallback({
    src: alertM4A,
    volume: 1.0,
    cooldownMs: 700,
  });

  const pushToast = React.useCallback(
    (t) => {
      const id = `${t.hitId || t.id || "h"}-${Date.now()}`;
      setToasts((prev) => [...prev, { ...t, _id: id }]);
      // Attempt sound — if m4a not unlocked yet, WebAudio fallback will work after the first gesture
      play();
      setTimeout(() => setToasts((prev) => prev.filter((x) => x._id !== id)), 30_000);
    },
    [play]
  );

  React.useEffect(() => {
    const onAlert = (ev) => {
      const p = ev?.detail || {};
      pushToast({
        hitId: p.hitId || p.public_ulid,
        vrm: p.vrm,
        note: p.note || "",
        listLabel: p.listLabel || p.type_label || null,
        color: p.color || p.type_color_hex || "#E74C3C",
      });
    };
    window.addEventListener("alert:hit", onAlert);
    return () => window.removeEventListener("alert:hit", onAlert);
  }, [pushToast]);

  // Dev helper
  if (typeof window !== "undefined") {
    window.__pushAlertToast = (o = {}) =>
      pushToast({
        hitId: o.hitId || `test-${Date.now()}`,
        vrm: o.vrm || "AB12 CDE",
        note: o.note ?? "Dev test toast",
        listLabel: o.listLabel ?? "VIP",
        color: o.color || "#F39C12",
      });
  }

  const visible = useStabilizedBool(toasts.length > 0, 80);

  return (
    <>
      {showUnlockPill && (
        <button
          type="button"
          onClick={requestUnlock}
          title={ready ? "Enable alert sound" : "Loading alert sound…"}
          disabled={!ready}
          style={{
            position: "fixed",
            top: 14,
            right: 14,
            zIndex: 1070,
            padding: "6px 10px",
            borderRadius: 999,
            background: "#111",
            color: "white",
            border: "1px solid rgba(255,255,255,.25)",
            boxShadow: "0 6px 18px rgba(0,0,0,.25)",
            cursor: ready ? "pointer" : "not-allowed",
            opacity: ready ? 0.9 : 0.6,
          }}
        >
          🔊 {ready ? "Enable sound" : "Preparing sound…"}
        </button>
      )}

      <div
        style={{
          position: "fixed",
          top: 14,
          right: 14,
          display: visible ? "flex" : "none",
          flexDirection: "column",
          gap: 10,
          zIndex: 1068,
          pointerEvents: "none",
        }}
        aria-live="polite"
      >
        {toasts.map((t) => (
          <div
            key={t._id}
            role="status"
            className="card"
            style={{
              pointerEvents: "auto",
              minWidth: 280,
              maxWidth: 420,
              borderRadius: 12,
              boxShadow: "0 10px 30px rgba(0,0,0,.18)",
              border: "1px solid rgba(0,0,0,.08)",
              overflow: "hidden",
            }}
          >
            <div
              className="card-header d-flex align-items-center justify-content-between"
              style={{
                background: `color-mix(in oklab, ${t.color} 18%, white)`,
                borderBottom: "1px solid rgba(0,0,0,.06)",
                padding: "10px 12px",
              }}
            >
              <div className="d-flex align-items-center" style={{ gap: 10 }}>
                <span
                  aria-hidden="true"
                  style={{
                    width: 10, height: 10, borderRadius: 999,
                    background: t.color || "#E74C3C",
                    boxShadow: "0 0 0 2px rgba(0,0,0,.06) inset",
                    display: "inline-block",
                  }}
                />
                <strong style={{ fontSize: ".95rem" }}>
                  {t.listLabel ? "Vehicle Alert" : "Watch list match"}
                </strong>
              </div>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setToasts((prev) => prev.filter((x) => x._id !== t._id))}
                aria-label="Dismiss alert"
              >
                Dismiss
              </button>
            </div>

            <div className="card-body" style={{ padding: "10px 12px", textAlign: "left" }}>
              <div style={{ marginBottom: 6 }}>
                <span className="registration-ui-front-small" style={{ display: "inline-block" }} aria-label={`Registration ${t.vrm}`}>
                  {t.vrm}
                </span>
              </div>
              {t.listLabel && (
                <div className="mb-1" style={{ fontSize: ".88rem" }}>
                  <strong>List:</strong> {t.listLabel}
                </div>
              )}
              {t.note && (
                <div className="text-muted" style={{ fontSize: ".88rem" }}>
                  <strong className="me-1">Note:</strong>
                  <span>{t.note}</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
